/**
 * Module-scoped chat stream lifecycle (WS6, multi-stream since Stage A).
 *
 * Streams deliberately live OUTSIDE the React tree: collapsing the
 * transcript unmounts the transcript column, and navigating ANYWHERE in the
 * app unmounts the whole session page — in both cases "the walkthrough
 * continues" (DESIGN §2.1; navigate-away round 2026-08-25 item 7): every
 * in-flight turn keeps pumping frames into its OWN chatStore live buffer
 * with no chat UI mounted, and the sidebar's session list statuses
 * (sessionActivity) show the way back. SEVERAL sessions may stream at once
 * (Map<sid, turn>); switching sessions aborts nothing. A stream ends in
 * exactly two places: the explicit Stop button (stopTurn(sid)) and logout
 * (handleShellUnmount aborts them all).
 *
 * Contract wiring (WS4):
 *  - frames  → chatStore.beginStream / ingestFrame / failStream (per sid;
 *    the live caption applies only for the ACTIVE session)
 *  - send    = the composer's Enter
 *  - transport drop without a terminal frame → attach to the turn again
 *    (it keeps running on the server, api.md §7, 2026-09-07); when that
 *    fails too, refetch the session and replay it
 *  - terminal (and message.start, where the server mints the title) →
 *    invalidate the session + its product's sessions list; transition
 *    sessionActivity (writing → unread/seen).
 */

import type { QueryClient } from '@tanstack/react-query';

import { invalidateSession, queryKeys } from '../../api/queries';
import {
  attachChatStream,
  streamChatMessage,
  type MessageFocus,
  type MessageMention,
} from '../../api/sse';
import { stopChatTurn } from './chatApi';
import type { ChatFrame, ChatSessionDetail, Ulid } from '../../api/types';
import { useSessionActivity } from '../../shell/sessionActivity';
import { liveFor, useChatStore } from '../../stores/chatStore';

interface ActiveTurn {
  sessionId: Ulid;
  controller: AbortController;
  userStopped: boolean;
  /** aborted by shell teardown (logout) — touch no store state */
  silent: boolean;
}

/** Every in-flight turn, keyed by session id (multi-stream). */
const activeTurns = new Map<Ulid, ActiveTurn>();

/**
 * Attaches still waiting to hear from the server (2026-09-08): a GET /stream
 * whose answer, a 204 or the first replayed frame, is on its way. Kept apart
 * from `activeTurns` so a question typed meanwhile is sent, not refused; the
 * send cuts the probe (startTurn), and a first frame promotes it (attachTurn).
 */
const probes = new Map<Ulid, ActiveTurn>();

// ---------------------------------------------------------------------------
// Replay-once guard (LRU)
// ---------------------------------------------------------------------------

/** How many sessions keep their replay mark + live buffer around. */
export const REPLAY_LRU_SIZE = 8;

/**
 * The guard LIVES IN chatStore (`replayedSessionIds`), not module scope,
 * because it protects state chatStore owns: `setActiveSession` wipes the
 * session's derived state, so it also forgets the incoming session's mark —
 * re-entering a session replays again. Viewport transitions (unmount/remount
 * of the transcript column) do NOT touch the store, so they never re-replay.
 * The list is an LRU of ~8: marking a ninth session evicts the oldest mark
 * AND its live buffer together (bounded memory across many sessions).
 */
export function hasReplayed(sessionId: Ulid): boolean {
  return useChatStore.getState().replayedSessionIds.includes(sessionId);
}

export function markReplayed(sessionId: Ulid): void {
  const state = useChatStore.getState();
  const ids = state.replayedSessionIds.filter((id) => id !== sessionId);
  ids.push(sessionId);
  const evicted = ids.length > REPLAY_LRU_SIZE ? ids.splice(0, ids.length - REPLAY_LRU_SIZE) : [];
  useChatStore.setState({ replayedSessionIds: ids });
  for (const id of evicted) {
    // Never evict a buffer that is still streaming — its mark is re-added
    // implicitly on the next replay pass after the turn settles.
    if (!activeTurns.has(id)) useChatStore.getState().clearLive(id);
  }
}

export function forgetReplay(sessionId: Ulid): void {
  const state = useChatStore.getState();
  if (!state.replayedSessionIds.includes(sessionId)) return;
  useChatStore.setState({
    replayedSessionIds: state.replayedSessionIds.filter((id) => id !== sessionId),
  });
}

// ---------------------------------------------------------------------------
// Turn bookkeeping
// ---------------------------------------------------------------------------

export function isTurnActiveFor(sessionId: Ulid): boolean {
  return activeTurns.has(sessionId);
}

/**
 * Whether a send for `sessionId` would be taken right now: no pump
 * registered for it and its live buffer not streaming. The test `startTurn`
 * makes before anything else, exposed so the composer can be answered at
 * once instead of when the turn settles minutes later. A probe (an attach
 * still waiting on the server) does not count: the send cuts it.
 */
export function canStartTurn(sessionId: Ulid): boolean {
  return !activeTurns.has(sessionId) && !liveFor(useChatStore.getState(), sessionId).streaming;
}

/** The owning product for a session, read off the query cache (may be null). */
function productIdFor(qc: QueryClient, sessionId: Ulid): Ulid | null {
  return qc.getQueryData<ChatSessionDetail>(queryKeys.session(sessionId))?.product_id ?? null;
}

/**
 * Re-sync the sidebar's session list for the product owning `sessionId`.
 * When the owning product is not derivable from the cache (a brand-new
 * session whose detail never loaded), every product's list refetches — the
 * title/ordering must not go stale just because the cache was cold.
 */
function invalidateSessionsList(qc: QueryClient, sessionId: Ulid): void {
  const pid = productIdFor(qc, sessionId);
  if (pid !== null) {
    void qc.invalidateQueries({ queryKey: queryKeys.sessions(pid) });
  } else {
    void qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'product' && q.queryKey[2] === 'sessions',
    });
  }
}

/**
 * Ingest one frame for `sessionId`: the live buffer takes it regardless of
 * which session is mounted; `message.start` re-syncs the
 * sessions list (the server mints the title at first-turn persist — the
 * sidebar should catch it early).
 */
function ingest(qc: QueryClient, sessionId: Ulid, frame: ChatFrame): void {
  useChatStore.getState().ingestFrame(sessionId, frame);
  if (frame.type === 'message.start') {
    invalidateSessionsList(qc, sessionId);
  }
}

/** A turn settled (terminal/stop/drop): activity + list/session re-sync. */
function settleTurn(qc: QueryClient, turn: ActiveTurn): Promise<unknown> {
  useSessionActivity.getState().markTerminal(turn.sessionId, { silent: turn.silent });
  if (turn.silent) return Promise.resolve();
  invalidateSessionsList(qc, turn.sessionId);
  return invalidateSession(qc, turn.sessionId);
}

/**
 * Send one user turn and pump its SSE stream. Resolves when the turn is
 * fully settled (terminal frame handled + session cache re-synced). Returns
 * false (no-op) when that session ALREADY has a turn in flight — other
 * sessions' streams are never touched.
 *
 * `focus` is what the question is about when it came from a set of parts
 * picked out of a diagram (round 8), `mentions` what the reader tagged
 * (DECISIONS #95): both ride the same POST as the text, and a question with
 * neither puts exactly the body on the wire it always has.
 */
export async function startTurn(
  qc: QueryClient,
  sessionId: Ulid,
  text: string,
  focus?: MessageFocus | null,
  mentions?: readonly MessageMention[] | null,
): Promise<boolean> {
  if (!canStartTurn(sessionId)) return false;
  // A mount's probe (attachTurn) still waiting on the server for this
  // session could only ever find the turn this send is about to start: cut
  // it, silently, before the POST goes out.
  const probe = probes.get(sessionId);
  if (probe !== undefined) {
    probes.delete(sessionId);
    probe.silent = true;
    probe.controller.abort();
  }

  const echoText = text;
  useChatStore.getState().patchLive(sessionId, { pendingUserText: echoText });
  useChatStore.getState().beginStream(sessionId);
  useSessionActivity.getState().markWriting(sessionId, productIdFor(qc, sessionId) ?? undefined);

  const turn: ActiveTurn = {
    sessionId,
    controller: new AbortController(),
    userStopped: false,
    silent: false,
  };
  activeTurns.set(sessionId, turn);

  try {
    const terminal = await streamChatMessage(
      sessionId,
      text,
      {
        onFrame: (frame) => {
          if (!turn.silent) ingest(qc, sessionId, frame);
        },
      },
      turn.controller.signal,
      focus,
      mentions,
    );
    if (terminal === null && !turn.silent) {
      if (turn.userStopped) {
        useChatStore.getState().failStream(sessionId, 'Stopped. Whatever was said is kept.');
      } else if (!turn.controller.signal.aborted) {
        // Transport drop: the turn is still running on the server
        // (2026-09-07). Attach to it again rather than declaring it lost.
        // The turn stays registered through the wait, so Stop still finds it
        // (review 2026-09-07); reattach hands the slot to the new pump.
        if (await reattach(qc, sessionId, turn)) return true;
        useChatStore
          .getState()
          .failStream(
            sessionId,
            turn.userStopped
              ? 'Stopped. Whatever was said is kept.'
              : 'The connection dropped. Restoring the saved turn.',
          );
      } else {
        useChatStore.getState().failStream(sessionId, 'Stopped.');
      }
      forgetReplay(sessionId); // full replay on next pass rebuilds canonical state
    }
    activeTurns.delete(sessionId);
    await settleTurn(qc, turn);
  } catch (err) {
    if (!turn.silent) {
      const message = err instanceof Error ? err.message : 'The stream failed.';
      useChatStore.getState().failStream(sessionId, message);
      // The same re-sync a drop gets: the server may hold part of the turn,
      // or have refused it outright (409 generation_active), and the
      // transcript has to show what it holds, not the buffer that failed.
      forgetReplay(sessionId);
      if (activeTurns.get(sessionId) === turn) activeTurns.delete(sessionId);
      await settleTurn(qc, turn);
    }
  } finally {
    if (liveFor(useChatStore.getState(), sessionId).pendingUserText === echoText) {
      useChatStore.getState().patchLive(sessionId, { pendingUserText: null });
    }
    if (activeTurns.get(sessionId) === turn) {
      activeTurns.delete(sessionId);
      useSessionActivity.getState().markTerminal(sessionId, { silent: turn.silent });
    }
  }
  return true;
}

/**
 * The Stop button — stops ONE session's turn, leaving every other stream
 * alone. The turn lives on the server (2026-09-07), so dropping our end of
 * the stream would not stop it: the server is told first, then the local
 * pump is released.
 */
export function stopTurn(sessionId: Ulid): void {
  // A resume probe (the GET /stream in flight after a drop) is not in
  // activeTurns yet, but the Stop button is showing: it must land there too.
  const turn = activeTurns.get(sessionId) ?? probes.get(sessionId);
  if (turn === undefined) return;
  turn.userStopped = true;
  void stopChatTurn(sessionId).catch(() => undefined);
  turn.controller.abort();
}

/** How long to wait before re-attaching after a transport drop, and how often. */
let reattachPolicy = { delayMs: 1000, tries: 3 };

/** Test seam: no real waits in a unit test. */
export function setReattachPolicyForTests(policy: { delayMs: number; tries: number }): void {
  reattachPolicy = policy;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * A stream we were pumping dropped without its terminal frame: the turn is
 * still running on the server. Attach again (a few tries, a second apart)
 * and keep pumping into the SAME live buffer, so the reader sees the answer
 * continue rather than a broken turn. Resolves true when the turn was
 * followed to its end this way.
 */
async function reattach(qc: QueryClient, sessionId: Ulid, prior: ActiveTurn): Promise<boolean> {
  for (let attempt = 0; attempt < reattachPolicy.tries; attempt += 1) {
    await sleep(reattachPolicy.delayMs);
    if (prior.silent || prior.userStopped) return false;
    // Hand the session's slot to the attach: the prior pump kept it through
    // the wait only so a Stop pressed meanwhile would still land.
    if (activeTurns.get(sessionId) === prior) activeTurns.delete(sessionId);
    const attached = await attachTurn(qc, sessionId, { resume: true });
    // 'busy': another pump (a mount, the sidebar) took the session during the
    // wait and is streaming it — nothing is lost, and nothing to restore.
    if (attached === 'busy') return true;
    // 'cut' mid-resume: only logout cuts a probe while the buffer still
    // streams (a send is refused then). The shell is gone; nothing is owed.
    if (attached === 'cut') {
      prior.silent = true;
      return true;
    }
    if (attached !== 'dropped') return attached === 'followed' || attached === 'idle';
    if (activeTurns.get(sessionId) === undefined) activeTurns.set(sessionId, prior);
  }
  if (activeTurns.get(sessionId) === prior) activeTurns.delete(sessionId);
  return false;
}

/**
 * Attach to a turn running on the server for this session (after a reload,
 * on opening a session the sidebar says is writing, or after a drop). The
 * frames it already emitted are replayed into a fresh live buffer, then the
 * rest stream in; a terminal frame settles the turn like any other.
 *
 * Until the server answers this is a PROBE, not a turn (2026-09-08): it sits
 * in `probes`, not `activeTurns`, so a question typed meanwhile is sent
 * rather than refused, and the send cuts it. The first replayed frame is
 * what registers the turn.
 *
 * Returns 'idle' when the server has nothing running there (the common
 * case: a cheap 204), 'followed' when the turn was followed to its terminal
 * frame, 'dropped' when this connection fell too, 'busy' when a pump or a
 * probe for this session already exists here, and 'cut' when a send (or
 * logout) ended the probe before the server said anything.
 */
export async function attachTurn(
  qc: QueryClient,
  sessionId: Ulid,
  opts: { resume?: boolean } = {},
): Promise<'idle' | 'followed' | 'dropped' | 'busy' | 'cut'> {
  if (activeTurns.has(sessionId) || probes.has(sessionId)) return 'busy';
  const turn: ActiveTurn = {
    sessionId,
    controller: new AbortController(),
    userStopped: false,
    silent: false,
  };
  probes.set(sessionId, turn);
  // The replay lands into a clean buffer: the server sends the whole turn
  // from message.start, so nothing already shown may be kept or the frames
  // would double. A resume after a drop clears too, for the same reason.
  let began = false;
  try {
    const terminal = await attachChatStream(
      sessionId,
      {
        onFrame: (frame) => {
          if (turn.silent) return;
          if (!began) {
            began = true;
            // The server has a turn here: the probe is the pump now.
            probes.delete(sessionId);
            activeTurns.set(sessionId, turn);
            useChatStore.getState().beginStream(sessionId);
            useSessionActivity
              .getState()
              .markWriting(sessionId, productIdFor(qc, sessionId) ?? undefined);
          }
          ingest(qc, sessionId, frame);
        },
      },
      turn.controller.signal,
    );
    // Cut before the server said anything: nothing was shown, nothing is
    // open, and whatever answer arrives now belongs to nobody.
    if (!began && turn.silent) return 'cut';
    if (terminal === undefined) {
      // A 204 is the server's word that nothing runs. A buffer still marked
      // streaming with no pump behind it is an orphan: the prior pump's after
      // a drop (review 2026-09-07), or a turn cut by a logout whose tab then
      // signed in again without a reload. Close it, then the saved turn
      // replaces it on the next pass; left open, it shows Stop with nothing
      // to stop and refuses every send.
      const orphaned =
        liveFor(useChatStore.getState(), sessionId).streaming && !activeTurns.has(sessionId);
      if (opts.resume === true || orphaned) {
        if (orphaned) {
          useChatStore
            .getState()
            .failStream(
              sessionId,
              opts.resume === true
                ? 'The answer finished while the connection was down. Showing the saved turn.'
                : 'Nothing is running any more. Showing the saved turn.',
            );
        }
        forgetReplay(sessionId);
        await settleTurn(qc, turn);
      }
      return 'idle';
    }
    if (terminal === null && !turn.silent) {
      if (turn.userStopped) {
        useChatStore.getState().failStream(sessionId, 'Stopped. Whatever was said is kept.');
        forgetReplay(sessionId);
        activeTurns.delete(sessionId);
        await settleTurn(qc, turn);
        return 'followed';
      }
      activeTurns.delete(sessionId);
      if (opts.resume !== true) {
        // A fresh attach (a reload, an opened session) dropped too: the turn
        // may still be running, so try again the way a POST drop does; only
        // when that fails is the buffer closed (review 2026-09-07: it used to
        // stay open with no pump, and the session read as writing forever).
        if (await reattach(qc, sessionId, turn)) return 'followed';
        if (began) {
          useChatStore
            .getState()
            .failStream(sessionId, 'The connection dropped. Restoring the saved turn.');
        }
        forgetReplay(sessionId);
        await settleTurn(qc, turn);
      }
      return 'dropped';
    }
    activeTurns.delete(sessionId);
    await settleTurn(qc, turn);
    return 'followed';
  } catch (err) {
    if (!turn.silent) {
      const message = err instanceof Error ? err.message : 'The stream failed.';
      if (began) useChatStore.getState().failStream(sessionId, message);
    }
    return 'dropped';
  } finally {
    if (probes.get(sessionId) === turn) probes.delete(sessionId);
    if (activeTurns.get(sessionId) === turn) {
      activeTurns.delete(sessionId);
      useSessionActivity.getState().markTerminal(sessionId, { silent: turn.silent });
    }
  }
}

/**
 * Authed-shell unmount (AppShell cleanup): the shell leaving the tree IS
 * logout — the only navigation that aborts streams, and it aborts ALL of
 * them. Silent: the per-user stores are being torn down with the session,
 * so no error line is owed to anyone.
 */
export function handleShellUnmount(): void {
  for (const turn of activeTurns.values()) {
    turn.silent = true;
    turn.controller.abort();
    useSessionActivity.getState().markTerminal(turn.sessionId, { silent: true });
  }
  activeTurns.clear();
  // A probe opened no buffer and marked nothing: it is only cut.
  for (const probe of probes.values()) {
    probe.silent = true;
    probe.controller.abort();
  }
  probes.clear();
}

/** Test-only: clear module state between tests. */
export function resetStreamControllerForTests(): void {
  for (const turn of [...activeTurns.values(), ...probes.values()]) {
    turn.silent = true;
    turn.controller.abort();
  }
  activeTurns.clear();
  probes.clear();
  useChatStore.setState({ live: {}, replayedSessionIds: [] });
  useSessionActivity.getState().reset();
  reattachPolicy = { delayMs: 1000, tries: 3 };
}
